import _ from "lodash";
import React from "react";
import moment from "moment";
import update from "immutability-helper";
import { getAcademicYearLabel } from "Utils";
import { getOutlinedIconComponent } from "Administrator/modules/Utils";

const iconStyle = {
  width: "20px",
  height: "20px",
};

// This function is for converting the flat list subject variants list to a nested structure
// subject groups > subjects > subject levels > response languages
const getSubjectVariants = ({ orgSubjectVariants }) => {
  const subjectsList = _.reduce(
    orgSubjectVariants,
    (result, { subjectGroup, subject, level, responseLanguages }) => {
      const { id: subjectGroupId, name: subjectGroupName, type } =
        subjectGroup || {};
      const {
        id: subjectId,
        name: subjectName,
        icon,
        contentSubject,
        ibOfferedSubject,
      } = subject || {};

      const assessmentYearTitle =
        ibOfferedSubject?.contentSubject?.assessmentYearTitle || // for org subject variants list
        contentSubject?.assessmentYearTitle; // for global subject variants list
      const { label, id: value } = level || {};
      const responseLanguageOptions = _.map(
        responseLanguages,
        ({ id, label }) => {
          return { label, value: id };
        }
      );

      const levelItem = {
        label,
        value,
        responseLanguages: responseLanguageOptions,
      };

      let updatedSubjectName = subjectName;
      if (!_.isEmpty(assessmentYearTitle))
        updatedSubjectName = `${subjectName} (${assessmentYearTitle})`;

      const iconComponent =
        icon &&
        getOutlinedIconComponent({
          icon,
          size: "small",
          containerStyle: iconStyle,
        });

      const subjectItem = {
        label: updatedSubjectName,
        value: subjectId,
        icon: iconComponent,
        levels: [levelItem],
        subjectGroupType: type,
      };

      const isSubjectGroupAdded = _.has(result, [subjectGroupId]);
      if (isSubjectGroupAdded) {
        const isSubjectAddedInSubjectGroup = _.has(
          result[subjectGroupId]["options"],
          [subjectId]
        );
        if (isSubjectAddedInSubjectGroup) {
          // adding new subject level to existing subject
          result = update(result, {
            [subjectGroupId]: {
              options: { [subjectId]: { levels: { $push: [levelItem] } } },
            },
          });
        } else {
          // adding new subject
          result = update(result, {
            [subjectGroupId]: {
              options: { [subjectId]: { $set: subjectItem } },
            },
          });
        }
      } else {
        // adding new subject group to final result
        const subjectGroupItem = {
          label: subjectGroupName,
          value: subjectGroupId,
          type,
          options: {
            [subjectId]: subjectItem,
          },
          displaySequence: _.get(subjectGroup, "displaySequence"),
        };
        result = update(result, {
          [subjectGroupId]: { $set: subjectGroupItem },
        });
      }
      return result;
    },
    {}
  );

  _.forOwn(subjectsList, group => {
    const options = _.get(group, "options", {});
    const sortedSubjects = _.orderBy(_.values(options), [
      subject => _.lowerCase(subject?.label),
    ]);
    group["options"] = sortedSubjects;
  });

  return _.values(subjectsList);
};

export const getSubjectVariantsMemoized = _.memoize(
  params => getSubjectVariants(params),
  ({ orgSubjectVariants }) => JSON.stringify({ orgSubjectVariants })
);

const getSubjectVariantsV2 = ({
  orgSubjectVariants,
  selectedSubjectDetails = {},
  includeArchived = false, // it will check whether to include archived subjects or variants in the list
}) => {
  const uniqueSubjects = _.uniqBy(
    orgSubjectVariants,
    variant => variant.subject.id
  );
  const {
    levelIds: selectedLevelIds,
    responseLanguageId: selectedResponseLanguageId,
    subjectId: selectedSubjectId,
  } = selectedSubjectDetails;

  const subjectsList = _.reduce(
    uniqueSubjects,
    (result, { subjectGroup, subject }) => {
      const {
        id: subjectGroupId,
        name: subjectGroupName,
        type,
        globalSubjectGroup,
        displaySequence,
      } = subjectGroup || {};
      const {
        id: subjectId,
        name: subjectName,
        icon,
        contentSubject,
        ibOfferedSubject,
        flatVariants,
        associatedLevels,
        isArchived: isSubjectArchived,
      } = subject || {};

      const shouldIncludeSubject = _.isEqual(selectedSubjectId, subjectId);
      if (includeArchived || !isSubjectArchived || shouldIncludeSubject) {
        const assessmentYearTitle =
          ibOfferedSubject?.contentSubject?.assessmentYearTitle || // for org subject variants list
          contentSubject?.assessmentYearTitle; // for global subject variants list

        const levels = _.reduce(
          flatVariants,
          (res, flatVariant) => {
            const {
              isArchived: isArchivedVaraint,
              level,
              responseLanguage,
            } = flatVariant;
            const responseLanguageOptions = {
              value: responseLanguage.id,
              label: responseLanguage.label,
              isArchived: isArchivedVaraint,
            };
            const levelItem = {
              label: _.get(level, "label", ""),
              value: _.get(level, "id", ""),
              responseLanguages: [responseLanguageOptions],
            };
            const includeVariant =
              shouldIncludeSubject &&
              _.includes(selectedLevelIds, levelItem.value) &&
              _.isEqual(
                selectedResponseLanguageId,
                responseLanguageOptions.value
              );
            if (includeArchived || !isArchivedVaraint || includeVariant) {
              const levelIndex = _.findIndex(res, {
                value: _.get(level, "id", ""),
              }); //check level already exist or not
              if (levelIndex > -1) {
                res = update(res, {
                  [levelIndex]: {
                    responseLanguages: { $push: [responseLanguageOptions] },
                  },
                });
              } else {
                res = update(res, {
                  $push: [levelItem],
                });
              }
            }
            return res;
          },
          []
        );

        let updatedSubjectName = subjectName;
        if (!_.isEmpty(assessmentYearTitle))
          updatedSubjectName = `${subjectName} - ${assessmentYearTitle}`;

        const iconComponent =
          icon &&
          getOutlinedIconComponent({
            icon,
            size: "small",
            containerStyle: iconStyle,
          });

        const subjectItem = {
          label: updatedSubjectName,
          value: subjectId,
          icon: iconComponent,
          levels: levels,
          subjectGroupType: type,
          associatedLevels: associatedLevels || [],
          globalSubjectGroup,
          isArchived: isSubjectArchived,
          ibOfferedSubject,
        };

        const isSubjectGroupAdded = _.has(result, [subjectGroupId]);
        if (isSubjectGroupAdded) {
          // adding new subject
          result = update(result, {
            [subjectGroupId]: {
              options: { [subjectId]: { $set: subjectItem } },
            },
          });
        } else {
          // adding new subject group to final result
          const subjectGroupItem = {
            label: subjectGroupName,
            value: subjectGroupId,
            type,
            options: {
              [subjectId]: subjectItem,
            },
            displaySequence: displaySequence,
          };
          result = update(result, {
            [subjectGroupId]: { $set: subjectGroupItem },
          });
        }
      }
      return result;
    },
    {}
  );

  _.forOwn(subjectsList, group => {
    const options = _.get(group, "options", {});
    const sortedSubjects = _.orderBy(_.values(options), [
      subject => _.lowerCase(subject?.label),
    ]);
    group["options"] = sortedSubjects;
  });

  return _.values(_.orderBy(subjectsList, "displaySequence"));
};

export const getSubjectVariantsMemoizedV2 = _.memoize(
  params => getSubjectVariantsV2(params),
  params => JSON.stringify(params)
);

const getGradeOptions = ({ grades }) => {
  return _.map(grades, ({ id, name }) => {
    return { label: name, value: id };
  });
};

export const getSubjectItem = ({
  name,
  id,
  icon,
  grades,
  isArchived,
  associatedLevels = [],
}) => {
  return {
    label: name,
    value: id,
    icon: getOutlinedIconComponent({
      icon,
      size: "small",
      containerStyle: iconStyle,
    }),
    grades: getGradeOptions({ grades }),
    associatedLevels,
    isArchived,
  };
};

export const getSubjectGroupSubjectList = ({
  subjectGroups,
  selectedSubjectId,
}) => {
  const subjectsOptionsList = _.reduce(
    subjectGroups,
    (result, { id, subjects, name }) => {
      const sortedSubjects = _.orderBy(subjects, [
        subject => subject?.isArchived || _.lowerCase(subject?.name),
      ]);
      const subjectGroup = { label: name, value: id };
      subjectGroup["options"] = _.reduce(
        sortedSubjects,
        (result, subject) => {
          if (!subject.isArchived || _.isEqual(subject.id, selectedSubjectId)) {
            result.push(getSubjectItem(subject));
          }
          return result;
        },
        []
      );
      result.push(subjectGroup);
      return result;
    },
    []
  );
  return subjectsOptionsList;
};

export const getSubjectGroupSubjectListMemoized = _.memoize(
  params => getSubjectGroupSubjectList(params),
  params => JSON.stringify(params)
);

export const getFlatSubjectList = ({ subjects, selectedSubjectIds = [] }) => {
  const sortedSubjects = _.orderBy(subjects, [
    subject => _.lowerCase(subject?.name),
  ]);
  const subjectList = _.reduce(
    sortedSubjects,
    (result, subject) => {
      const { isArchived } = subject;
      if (!isArchived || _.includes(selectedSubjectIds, subject.id)) {
        result.push(getSubjectItem(subject));
      }
      return result;
    },
    []
  );
  return subjectList;
};

export const getRequiredFields = ({
  selectedCurriculumObj,
  isSubjectWithNoVariants,
}) => {
  const { isPYP, isDP } = selectedCurriculumObj;

  const requiredFields = ["title", "grades"];
  if (!isPYP) requiredFields.push("subject");
  if (isDP) requiredFields.push("academicYears");
  if (isDP && !isSubjectWithNoVariants)
    requiredFields.push("subjectLevels", "responseLanguages");
  return requiredFields;
};

export const getGradeAcademicYears = ({
  params,
  yearGroup,
  orgYearGroups,
  organizationAcademicYears,
}) => {
  const gradeYearGroup = yearGroup
    ? yearGroup
    : _.find(orgYearGroups, item => {
        return _.isEqual(_.get(item, "grade.id"), _.get(params, "value"));
      });

  const yearGroupFinalYear = _.get(gradeYearGroup, "examSession.year", "");
  const finalAcademicYearIdx = _.findIndex(organizationAcademicYears, item => {
    return _.isEqual(
      _.toNumber(yearGroupFinalYear),
      moment(_.get(item, "endDate", "")).year()
    );
  });

  const academicYearsList = [];
  _.forEach([finalAcademicYearIdx - 1, finalAcademicYearIdx], index => {
    const {
      id,
      startDate,
      endDate,
      isCurrentAcademicYear,
      isPreviousAcademicYear,
      terms,
    } = _.get(organizationAcademicYears, [index], {});
    const label = getAcademicYearLabel({ startDate, endDate });
    if (id)
      academicYearsList.push({
        label,
        value: id,
        startDate,
        endDate,
        id,
        key: id,
        isCurrentAcademicYear,
        isPreviousAcademicYear,
        terms,
      });
  });

  return academicYearsList;
};
